/**
 * BadgeManager
 *
 * 앱 아이콘 뱃지와 페이지 타이틀의 알림 카운트를 관리하는 클래스입니다.
 * Badge API를 지원하는 브라우저에서는 Badge API를 사용하고,
 * 지원하지 않는 브라우저에서는 페이지 타이틀에 카운트를 표시합니다.
 */

class BadgeManager {
  constructor() {
    this.count = 0;
    this.originalTitle = document.title;
    this.storageKey = 'badge_count';

    // 로컬 스토리지에서 저장된 카운트 복원
    this.loadCount();
  }

  /**
   * 로컬 스토리지에서 뱃지 카운트를 로드합니다.
   */
  loadCount() {
    const savedCount = localStorage.getItem(this.storageKey);
    if (savedCount !== null) {
      const parsedCount = parseInt(savedCount, 10);
      if (!isNaN(parsedCount) && parsedCount >= 0) {
        this.count = parsedCount;
        this.updateBadge();
      }
    }
  }

  /**
   * 로컬 스토리지에 뱃지 카운트를 저장합니다.
   */
  saveCount() {
    localStorage.setItem(this.storageKey, this.count.toString());
  }

  /**
   * 뱃지 카운트를 1 증가시킵니다.
   */
  increment() {
    this.count++;
    this.saveCount();
    this.updateBadge();
  }

  /**
   * 뱃지 카운트를 1 감소시킵니다.
   * 카운트가 0보다 작아지지 않도록 보장합니다.
   */
  decrement() {
    if (this.count > 0) {
      this.count--;
      this.saveCount();
      this.updateBadge();
    }
  }

  /**
   * 뱃지 카운트를 특정 값으로 설정합니다.
   * @param {number} count - 설정할 카운트 값 (0 이상)
   */
  setCount(count) {
    if (typeof count === 'number' && count >= 0) {
      this.count = Math.floor(count);
      this.saveCount();
      this.updateBadge();
    } else {
      console.warn('유효하지 않은 카운트 값:', count);
    }
  }

  /**
   * 현재 뱃지 카운트를 반환합니다.
   * @returns {number} 현재 카운트
   */
  getCount() {
    return this.count;
  }

  /**
   * Badge API가 지원되는지 확인합니다.
   * @returns {boolean} Badge API 지원 여부
   */
  isBadgeAPISupported() {
    return 'setAppBadge' in navigator && 'clearAppBadge' in navigator;
  }

  /**
   * 뱃지를 업데이트합니다.
   * Badge API를 지원하는 경우 Badge API를 사용하고,
   * 지원하지 않는 경우 페이지 타이틀에 카운트를 표시합니다.
   */
  updateBadge() {
    if (this.count > 0) {
      // 카운트가 0보다 크면 뱃지 표시
      if (this.isBadgeAPISupported()) {
        navigator.setAppBadge(this.count).catch(() => this.setTitleBadge(this.count));
      } else {
        this.setTitleBadge(this.count);
      }
    } else {
      // 카운트가 0이면 뱃지 제거
      this.clearBadge();
    }
  }

  /**
   * 페이지 타이틀에 뱃지 카운트를 표시합니다.
   * @param {number} count - 표시할 카운트
   */
  setTitleBadge(count) {
    const cleanTitle = this.originalTitle.replace(/^\(\d+\)\s*/, '');
    document.title = `(${count}) ${cleanTitle}`;
  }

  /**
   * 뱃지를 제거합니다.
   * Badge API를 지원하는 경우 Badge API를 사용하고,
   * 지원하지 않는 경우 페이지 타이틀을 원래대로 복원합니다.
   */
  clearBadge() {
    if (this.isBadgeAPISupported()) {
      navigator.clearAppBadge().catch(() => this.clearTitleBadge());
    } else {
      this.clearTitleBadge();
    }
  }

  /**
   * 페이지 타이틀에서 뱃지를 제거합니다.
   */
  clearTitleBadge() {
    document.title = this.originalTitle.replace(/^\(\d+\)\s*/, '');
  }

  /**
   * 원래 페이지 타이틀을 업데이트합니다.
   * 페이지 타이틀이 동적으로 변경되는 경우 사용합니다.
   * @param {string} newTitle - 새로운 원래 타이틀
   */
  setOriginalTitle(newTitle) {
    if (typeof newTitle === 'string') {
      this.originalTitle = newTitle;
      this.updateBadge();
    }
  }

  /**
   * BadgeManager를 초기화합니다.
   * 카운트를 0으로 설정하고 뱃지를 제거합니다.
   */
  reset() {
    this.count = 0;
    this.saveCount();
    this.clearBadge();
  }
}

// 싱글톤 인스턴스 생성 및 내보내기
const badgeManager = new BadgeManager();
export default badgeManager;
